import {
  IPublicClientApplication,
  AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { tokenRequest } from '@/authConfig';

// Module-level guard: once an interactive redirect is triggered the page is
// navigating away, so concurrent callers must not each fire another redirect.
let redirecting = false;

/**
 * Acquire an API access token for the backend, refreshing silently when the
 * cached token is expired.
 *
 * When silent renewal fails because the identity-provider session no longer
 * exists (InteractionRequiredAuthError, e.g. AADSTS160021), this triggers a
 * full-page redirect to Microsoft to re-authenticate instead of silently
 * returning no token and letting the request 401. On return, MsalProvider
 * consumes the redirect and the original navigation resumes authenticated.
 *
 * Returns the access token, or null when there is no account or a redirect is
 * in progress (in which case the page is navigating away and callers should
 * abort the request).
 */
export async function acquireApiToken(
  instance: IPublicClientApplication,
  account: AccountInfo | undefined,
  request: { scopes: string[] } = tokenRequest,
): Promise<string | null> {
  if (!account) return null;

  try {
    const resp = await instance.acquireTokenSilent({ ...request, account });
    return resp.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      if (!redirecting) {
        redirecting = true;
        // Fire and forget: the page navigates to the IdP. Reset the guard only
        // if the redirect itself fails to launch.
        instance.acquireTokenRedirect({ ...request, account }).catch(() => {
          redirecting = false;
        });
      }
      return null;
    }
    // Non-interactive failure (network blip, transient). Preserve the prior
    // behavior of proceeding without a token rather than hard-failing the hook.
    console.error('acquireApiToken: silent token acquisition failed', err);
    return null;
  }
}
